import { useCallback, useMemo, useRef, useState } from 'react';
import {
  quantizeWebsiteMediaFrame,
  resolveWebsiteMediaFrame,
  setWebsiteMediaFrame,
  websiteMediaFrameDevices,
  websiteMediaFramingKeyFor,
  WEBSITE_MEDIA_FRAMING_KEYS,
  type WebsiteFrameDevice,
  type WebsiteMediaFrame,
  type WebsiteMediaFrameEntry,
  type WebsiteMediaFraming,
} from '@dukanti/shared';

export interface UseFramingDraftArgs {
  /** The framing of the value in the dialog (applied, maybe not saved yet). */
  readonly committed: WebsiteMediaFraming | undefined;
  readonly entry: WebsiteMediaFrameEntry;
  /** The canvas device: the tab the editor opens on (PRD §8.4 "do not fight"). */
  readonly initialDevice: WebsiteFrameDevice;
  /** What each device shows with no frame of its own (the family CSS default, PD7). */
  readonly defaults: Readonly<Record<WebsiteFrameDevice, WebsiteMediaFrame>>;
  /**
   * UI/UX P2-3: the slot is for this one device (a hero's mobile or desktop
   * image). The editor frames only that device: no tabs, and the stored
   * framing of the other device is kept as it is.
   */
  readonly slotDevice?: WebsiteFrameDevice | null;
}

export interface FramingDraft {
  /** Two devices for a `perDevice` slot that renders everywhere (two tabs), else one (no tabs). */
  readonly devices: readonly WebsiteFrameDevice[];
  readonly tab: WebsiteFrameDevice;
  readonly setTab: (device: WebsiteFrameDevice) => void;
  /** The framing the merchant sees: the local edit, else the committed one. */
  readonly draft: WebsiteMediaFraming | undefined;
  /** The frame of the active tab: its own, the shared one, else the default. */
  readonly frame: WebsiteMediaFrame;
  /** The active tab shows the default (no frame of its own and no shared frame). */
  readonly isDefault: boolean;
  /** Changes the active tab's frame from the latest frame (drag, keys, zoom). */
  readonly update: (change: (frame: WebsiteMediaFrame) => WebsiteMediaFrame) => void;
  /** Back to the default for the active tab. Local: it needs Apply like any edit. */
  readonly reset: () => void;
  /** Drops the local edit of every tab. */
  readonly cancel: () => void;
  /** The local edit, ready for Apply; then the draft follows the committed value again. */
  readonly takeApplied: () => WebsiteMediaFraming | undefined;
  /** The local edit differs from the committed framing (Apply/Cancel are offered). */
  readonly dirty: boolean;
}

function framesEqual(a: WebsiteMediaFrame | undefined, b: WebsiteMediaFrame | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  const x = quantizeWebsiteMediaFrame(a);
  const y = quantizeWebsiteMediaFrame(b);
  return x.x === y.x && x.y === y.y && x.zoom === y.zoom;
}

/** Same frames under the same keys (key order free). */
export function websiteMediaFramingsEqual(
  a: WebsiteMediaFraming | undefined,
  b: WebsiteMediaFraming | undefined,
): boolean {
  return WEBSITE_MEDIA_FRAMING_KEYS.every((key) => framesEqual(a?.[key], b?.[key]));
}

/**
 * PD2: a `perDevice` slot writes `desktop`/`mobile`, every other slot writes
 * `shared`, and the editor never leaves `shared` beside a device key. A
 * stored `shared` frame of a `perDevice` slot first becomes the frame of each
 * device that has none, so editing one device does not move the other.
 *
 * UI/UX P2-3: a `perDevice` slot edited for its one device (`slotDevice`)
 * writes only that device's key; every other stored key (its backup
 * framing for the other device, a `shared` one) stays exactly as it is.
 */
export function writeWebsiteMediaFrame(
  framing: WebsiteMediaFraming | undefined,
  entry: WebsiteMediaFrameEntry,
  device: WebsiteFrameDevice,
  frame: WebsiteMediaFrame | null,
  slotDevice: WebsiteFrameDevice | null = null,
): WebsiteMediaFraming | undefined {
  const key = websiteMediaFramingKeyFor(entry, device);
  if (key === 'shared') return setWebsiteMediaFrame(undefined, 'shared', frame);
  if (slotDevice !== null) return setWebsiteMediaFrame(framing, key, frame);
  let base = framing;
  if (base?.shared) {
    const shared = base.shared;
    base = setWebsiteMediaFrame(
      setWebsiteMediaFrame(undefined, 'desktop', base.desktop ?? shared),
      'mobile',
      base.mobile ?? shared,
    );
  }
  return setWebsiteMediaFrame(base, key, frame);
}

interface Local {
  readonly framing: WebsiteMediaFraming | undefined;
}

/**
 * Epic #228 P6: the tentative crop of one media field (PD6). Drags, keys,
 * zoom and Reset change a local framing only; nothing reaches the queue
 * until Apply. Each device tab keeps its own frame: switching tabs never
 * resets the other one.
 *
 * With no local edit the draft IS the committed framing, so a value that
 * changes under the editor (Apply, a reload) is shown as it is and never
 * reads as a local edit.
 */
export function useFramingDraft({
  committed,
  entry,
  initialDevice,
  defaults,
  slotDevice = null,
}: UseFramingDraftArgs): FramingDraft {
  const devices = useMemo(
    () => (slotDevice ? [slotDevice] : websiteMediaFrameDevices(entry)),
    [entry, slotDevice],
  );
  const [tab, setTabState] = useState<WebsiteFrameDevice>(() =>
    devices.includes(initialDevice) ? initialDevice : (devices[0] ?? 'desktop'),
  );
  const activeTab = devices.includes(tab) ? tab : (devices[0] ?? 'desktop');
  const [local, setLocal] = useState<Local | null>(null);

  // A local edit equal to the committed framing is no edit (the value may
  // have caught up with it): the draft follows the value.
  const dirty = local !== null && !websiteMediaFramingsEqual(local.framing, committed);
  const draft = dirty ? local.framing : committed;
  const own = resolveWebsiteMediaFrame(draft, activeTab);
  const frame = own ?? defaults[activeTab];

  // Drags arrive faster than renders: read and write the latest values.
  const latest = useRef({ draft, frame, activeTab, committed });
  latest.current = { draft, frame, activeTab, committed };

  const setTab = useCallback(
    (device: WebsiteFrameDevice): void => {
      if (devices.includes(device)) setTabState(device);
    },
    [devices],
  );

  const write = useCallback(
    (next: WebsiteMediaFrame | null): void => {
      const { draft: currentDraft, activeTab: device } = latest.current;
      const framing = writeWebsiteMediaFrame(currentDraft, entry, device, next, slotDevice);
      latest.current = {
        ...latest.current,
        draft: framing,
        frame: resolveWebsiteMediaFrame(framing, device) ?? defaults[device],
      };
      // Back to the committed framing: no local edit, the draft follows the value again.
      setLocal(websiteMediaFramingsEqual(framing, latest.current.committed) ? null : { framing });
    },
    [defaults, entry, slotDevice],
  );

  const update = useCallback(
    (change: (frame: WebsiteMediaFrame) => WebsiteMediaFrame): void => {
      write(change(latest.current.frame));
    },
    [write],
  );

  const reset = useCallback((): void => write(null), [write]);
  const cancel = useCallback((): void => setLocal(null), []);
  const takeApplied = useCallback((): WebsiteMediaFraming | undefined => {
    const applied = latest.current.draft;
    setLocal(null);
    return applied;
  }, []);

  return {
    devices,
    tab: activeTab,
    setTab,
    draft,
    frame,
    isDefault: own === null,
    update,
    reset,
    cancel,
    takeApplied,
    dirty,
  };
}
